import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Updater } from 'use-immer'
import { ConfigProps } from '../../page/Map'
import {
    CircularFilterConfig,
    CircularOption,
} from '../../../type/SubjectProps'
import { fetchCircularOptions } from '../../../utils/fetchCircular'
import filterStyles from '../Filter/Filter.module.scss'
import styles from './CircularFilter.module.scss'

interface Props {
    config: ConfigProps
    updateConfig: Updater<ConfigProps>
    filters: CircularFilterConfig
    setFilters: React.Dispatch<React.SetStateAction<CircularFilterConfig>>
    className?: string
}

export const CircularFilter = ({
    config,
    updateConfig,
    filters,
    setFilters,
    className,
}: Props) => {
    const [options, setOptions] = useState<{
        activities: CircularOption[]
        districts: CircularOption[]
    }>({ activities: [], districts: [] })
    const [state, setState] = useState('loading')
    const [retry, setRetry] = useState(0)
    useEffect(() => {
        const controller = new AbortController()
        setState('loading')
        fetchCircularOptions(controller.signal)
            .then((result) => {
                if (controller.signal.aborted) return
                setOptions(result)
                setState('ready')
            })
            .catch(() => {
                if (!controller.signal.aborted) setState('error')
            })
        return () => controller.abort()
    }, [retry])

    const count = filters.activities.length + filters.districts.length
    return (
        <div
            className={clsx(
                filterStyles.wrapper,
                config.filterOpen && filterStyles.open,
                className
            )}
        >
            <button
                className={filterStyles.toggle}
                aria-expanded={config.filterOpen}
                aria-controls="circular-filters"
                onClick={() =>
                    updateConfig((draft) => {
                        draft.filterOpen = !draft.filterOpen
                        if (draft.filterOpen) draft.resultListOpen = false
                    })
                }
            >
                <span className={filterStyles.toggleIcon}>
                    <img
                        className={filterStyles.toggleIconImg}
                        src="/img/icon/filter.svg"
                        alt=""
                    />
                </span>
                <span className={filterStyles.toggleText}>
                    Filtry{count > 0 ? ` (${count})` : ''}
                </span>
            </button>
            {config.filterOpen && (
                <div id="circular-filters" className={styles.content}>
                    {state === 'loading' && (
                        <p role="status" className={styles.message}>
                            Načítání filtrů…
                        </p>
                    )}
                    {state === 'error' && (
                        <div role="alert" className={styles.message}>
                            Filtry se nepodařilo načíst.
                            <button
                                className={styles.reset}
                                onClick={() => setRetry((value) => value + 1)}
                            >
                                Zkusit znovu
                            </button>
                        </div>
                    )}
                    {state === 'ready' && (
                        <>
                            {(
                                [
                                    { key: 'activities', label: 'Subjekty' },
                                    { key: 'districts', label: 'Městská část' },
                                ] as const
                            ).map(({ key, label }) => (
                                <details key={key} className={styles.group}>
                                    <summary>
                                        <img
                                            className={styles.groupIcon}
                                            src={`/img/layout/circular-filter-${
                                                key === 'activities'
                                                    ? 'subjects'
                                                    : 'districts'
                                            }.svg`}
                                            alt=""
                                        />
                                        <span>
                                            {label}
                                            {filters[key].length > 0
                                                ? ` (${filters[key].length})`
                                                : ''}
                                        </span>
                                        <img
                                            className={styles.groupArrow}
                                            src="/img/layout/accordion-arrow.svg"
                                            alt=""
                                        />
                                    </summary>
                                    {options[key].length === 0 && (
                                        <p className={styles.message}>
                                            Žádné možnosti
                                        </p>
                                    )}
                                    {options[key].map((option) => (
                                        <label
                                            className={styles.option}
                                            key={option.documentId}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={filters[key].includes(
                                                    option.documentId
                                                )}
                                                onChange={() =>
                                                    setFilters((previous) => ({
                                                        ...previous,
                                                        [key]: previous[
                                                            key
                                                        ].includes(
                                                            option.documentId
                                                        )
                                                            ? previous[
                                                                  key
                                                              ].filter(
                                                                  (id) =>
                                                                      id !==
                                                                      option.documentId
                                                              )
                                                            : [
                                                                  ...previous[
                                                                      key
                                                                  ],
                                                                  option.documentId,
                                                              ],
                                                    }))
                                                }
                                            />
                                            <span>{option.name}</span>
                                        </label>
                                    ))}
                                </details>
                            ))}
                            {count > 0 && (
                                <button
                                    className={styles.reset}
                                    onClick={() =>
                                        setFilters({
                                            activities: [],
                                            districts: [],
                                        })
                                    }
                                >
                                    Zrušit filtry
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}
